import React from 'react';
import { motion } from 'framer-motion';
import { siteConfig, profile } from '../data';
import { CheckCircle2, Heart, Award, Zap, ShieldCheck, GraduationCap } from 'lucide-react';

const About = () => {
  return (
    <div className="bg-white">
      {/* Header Section */}
      <section className="py-12 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-6 tracking-tight">
              Mød din coach: <br className="hidden md:block" />
              <span className="text-emerald-600">{profile.name}</span>
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed">
              Min vision er at skabe en bredere og dybere forståelse af, hvad menneskelig trivsel egentlig er. Både for individet, men i høj grad også for fællesskabet.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <div className="relative">
                <img
                  src="/materiale-260501/portræt/image00005.jpg"
                  alt={profile.name}
                  className="shadow-2xl grayscale-[10%] aspect-square object-cover w-full"
                />
                <div className="absolute -bottom-6 -right-6 bg-emerald-900 text-white p-6 hidden md:block max-w-[280px] shadow-xl">
                  <p className="italic text-base mb-3 text-emerald-50">
                    "Alt starter med at tage ansvar for eget liv."
                  </p>
                  <p className="font-bold text-white text-sm">— {profile.name}</p>
                </div>
              </div>
            </motion.div>

            <div className="order-1 lg:order-2 space-y-8">
              <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 leading-tight">Uddannelse & <span className="text-emerald-600">Erfaring</span></h2>
              <div className="space-y-6 text-slate-600 text-lg leading-relaxed">
                <div className="space-y-4">
                  {profile.certifications.map((cert, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <GraduationCap className="h-6 w-6 text-emerald-700 shrink-0 mt-1" />
                      <span className="text-zinc-800 font-medium">{cert}</span>
                    </div>
                  ))}
                </div>
                <p>
                  Mit arbejde bygger på forståelsen af, at vi som mennesker er flokdyr med primitive behov. Trivsel er ikke bare fravær af stress – det er en aktiv tilstand, der kræver balance mellem vores biologi, vores mindset og de strategier, vi bruger i hverdagen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Second Section (Text Only) */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="space-y-8">
              <h2 className="text-3xl md:text-4xl font-bold text-black leading-tight">Min <span className="text-emerald-600">Vision & Mission</span></h2>
              <div className="space-y-6 text-black text-lg leading-relaxed">
                <p>
                  Udfordringer starter ofte i os selv og de rummer de største muligheder for vækst.
                </p>
                <p>
                  Min mission er at gøre dig stærkere, så du ikke bare trives i hverdagen, men også bliver i stand til at støtte andre omkring dig.
                </p>
                <ul className="space-y-3">
                  {[
                    'Trivlogi-metoden (Biologi, Mindset, Strategi)',
                    'Individuel sparring 24/7 under forløb',
                    'Fokus på både individ og fællesskab'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center text-black font-medium">
                      <CheckCircle2 className="h-5 w-5 text-emerald-900 mr-3" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Values Section */}
      <section className="py-16 bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-16">Mine kerneværdier</h2>
          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                title: 'Ansvar',
                description: 'At tage ansvar er det første skridt mod frihed og forandring.'
              },
              {
                title: 'Dybde',
                description: 'Jo mere tid jo mere dybde, jo mere dybde jo mere værdi.'
              },
              {
                title: 'Videnskab',
                description: 'Vi arbejder med biologiske og videnskabelige forklaringer på trivsel.'
              }
            ].map((value, i) => (
              <div key={i} className="p-8 border border-white/20">
                <h3 className="text-2xl font-bold mb-4 text-white">{value.title}</h3>
                <p className="text-emerald-50/70 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mood Gallery Section */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative h-[400px]"
            >
              <img
                src="/materiale-260501/andet/image00008.jpg"
                alt="Detalje fra kontoret"
                className="w-full h-full object-cover shadow-lg"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="relative h-[400px] flex flex-col justify-center"
            >
              <div className="p-8 bg-zinc-50 border border-zinc-100">
                <h3 className="text-2xl font-bold text-zinc-900 mb-4 italic">Plads til ro og fordybelse</h3>
                <p className="text-slate-600 text-lg leading-relaxed mb-6">
                  Når vi mødes, er det i rammer, der inviterer til at sænke paraderne og kigge indad. Detaljerne betyder noget, for de er med til at skabe den tryghed, der er fundamentet for forandring.
                </p>
                <div className="h-64 relative overflow-hidden">
                   <img
                    src="/materiale-260501/andet/image00004.jpg"
                    alt="Stemningsbillede"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
